import { useState } from 'react';

import classNames from 'classnames';

import type { BehandlingsvalgSammenlikningBlockDto } from '../../types/cms';

import FormGroup from '@helsenorge/designsystem-react/components/FormGroup';
import Radio from '@helsenorge/designsystem-react/components/Radio';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Title from '@helsenorge/designsystem-react/components/Title';

import BehandlingsvalgSammenlikningBlock, {
  type BehandlingsvalgSammenlikningBlockAppearance,
} from '@helsenorge/core-cms/behandlingsvalg/behandlingsvalgsammenlikningblock';
import type { PortableTextBlocks } from '@helsenorge/core-cms/richtext/portable-text-utils';

import styles from './styles.module.scss';

interface Props {
  showComparisonAsTable?: boolean;
  showTreatmentLinks?: boolean;
  sammenlikningsBlockArray?: BehandlingsvalgSammenlikningBlockDto[];
  procedureTitle: string;
  benefitsTitle: string;
  disadvantagesTitle: string;
  selectionLegend: string;
  cardSelectionTitle: string;
  tableSelectionTitle: string;
}

const BehandlingsvalgSammenlikningWrapper: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  showComparisonAsTable,
  showTreatmentLinks = true,
  sammenlikningsBlockArray,
  procedureTitle,
  benefitsTitle,
  disadvantagesTitle,
  selectionLegend,
  cardSelectionTitle,
  tableSelectionTitle,
}: React.PropsWithChildren<Props>) => {
  const isSingleBlock = sammenlikningsBlockArray && sammenlikningsBlockArray.length === 1;
  const [displayStyle, setDisplayStyle] = useState<BehandlingsvalgSammenlikningBlockAppearance>(isSingleBlock ? 'card' : 'table');

  const handleRadioChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    setDisplayStyle(event.target.value as BehandlingsvalgSammenlikningBlockAppearance);
  };

  const renderBehandlingsvalgSammenlikningBlock = (
    sammenlikningsBlock: BehandlingsvalgSammenlikningBlockDto,
    key: number,
    content?: PortableTextBlocks
  ): React.ReactNode => {
    if (!content) {
      return null;
    }
    return (
      <div key={key} className={classNames('col-lg-6', styles['behandlingsvalgsammenlikning-block-wrapper'])}>
        {!isSingleBlock && <Spacer size={'m'} />}
        <BehandlingsvalgSammenlikningBlock
          title={sammenlikningsBlock.title}
          icon={sammenlikningsBlock.icon}
          implementation={content}
          treatmentUrl={sammenlikningsBlock.treatmentUrl}
          treatmentUrlTitle={sammenlikningsBlock.treatmentUrlTitle}
          appearance={displayStyle}
          showTreatmentLinks={showTreatmentLinks}
        />
      </div>
    );
  };

  return (
    <>
      {!showComparisonAsTable && !isSingleBlock && (
        <FormGroup legend={selectionLegend}>
          <Radio
            name="compare-options"
            label={cardSelectionTitle}
            onChange={handleRadioChange}
            value={'card'}
            defaultChecked={displayStyle === 'card'}
          />
          <Radio
            name="compare-options"
            label={tableSelectionTitle}
            onChange={handleRadioChange}
            value={'table'}
            defaultChecked={displayStyle === 'table'}
          />
        </FormGroup>
      )}
      {sammenlikningsBlockArray && displayStyle === 'table' && (
        <div>
          <Title margin={1} htmlMarkup={'h2'} appearance={'title2'}>
            {procedureTitle}
          </Title>
          <div className={'row'}>
            {sammenlikningsBlockArray.map((sammenlikningsBlock, index) =>
              renderBehandlingsvalgSammenlikningBlock(sammenlikningsBlock, index, sammenlikningsBlock.implementation)
            )}
          </div>

          <Title margin={1} htmlMarkup={'h2'} appearance={'title2'}>
            {benefitsTitle}
          </Title>
          <div className={'row'}>
            {sammenlikningsBlockArray.map((sammenlikningsBlock, index) =>
              renderBehandlingsvalgSammenlikningBlock(sammenlikningsBlock, index, sammenlikningsBlock.advantages)
            )}
          </div>

          <Title margin={1} htmlMarkup={'h2'} appearance={'title2'}>
            {disadvantagesTitle}
          </Title>
          <div className={'row'}>
            {sammenlikningsBlockArray.map((sammenlikningsBlock, index) =>
              renderBehandlingsvalgSammenlikningBlock(sammenlikningsBlock, index, sammenlikningsBlock.disadvantages)
            )}
          </div>
        </div>
      )}
      {sammenlikningsBlockArray &&
        displayStyle === 'card' &&
        sammenlikningsBlockArray.map((sammenlikningsBlock, index) => {
          return (
            <div key={index}>
              {!isSingleBlock && <Spacer size={'m'} />}
              <BehandlingsvalgSammenlikningBlock
                title={sammenlikningsBlock.title}
                icon={sammenlikningsBlock.icon}
                implementationHeader={sammenlikningsBlock.implementationHeader}
                implementation={sammenlikningsBlock.implementation}
                advantagesHeader={sammenlikningsBlock.advantagesHeader}
                advantages={sammenlikningsBlock.advantages}
                disadvantagesHeader={sammenlikningsBlock.disadvantagesHeader}
                disadvantages={sammenlikningsBlock.disadvantages}
                treatmentUrl={sammenlikningsBlock.treatmentUrl}
                treatmentUrlTitle={sammenlikningsBlock.treatmentUrlTitle}
                appearance={displayStyle}
                showTreatmentLinks={showTreatmentLinks}
              />
            </div>
          );
        })}
    </>
  );
};

export default BehandlingsvalgSammenlikningWrapper;
